'use client';

import React, { type ReactNode } from 'react';
import { useLegacyC } from '../theme';
import { space, fontSize, fontWeight, radius, size } from '../theme/tokens';
import { Icon } from './icons/Icon';

export interface BackHeaderProps {
  /** Screen title, centered. */
  title: string;
  /** Called when the back chevron is tapped. */
  onBack: () => void;
  /** Accessible label for the back button. Default "Zurück". */
  backLabel?: string;
  /** Optional right-hand slot (e.g. a clear/reset action). */
  action?: ReactNode;
}

/**
 * Persistent header for pushed detail-screens (Coach-Chat, Foto-Analyse,
 * Mahlzeit-Detail, aktives Training, Fortschritt). Mirrors the app-shell
 * header: carries the safe-area-top inset, a `chevronLeft` back affordance
 * on the left, a centered title, and an optional action on the right.
 *
 * The 44px tap targets on both edges keep the centered title optically
 * balanced even when only one side has an action.
 */
export function BackHeader({ title, onBack, backLabel = 'Zurück', action }: BackHeaderProps) {
  const C = useLegacyC();

  return (
    <div
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: space[2],
        padding: `calc(env(safe-area-inset-top, 0px) + ${space[3]}px) ${space[5]}px ${space[3]}px`,
        background: C.bg,
        borderBottom: `1px solid ${C.bd}`,
      }}
    >
      <button
        onClick={onBack}
        aria-label={backLabel}
        style={{
          width: size.tap,
          height: size.tap,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: radius.lg,
          border: 'none',
          background: 'none',
          color: C.text,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <Icon name="chevronLeft" size={size.icon.lg} />
      </button>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: 'center',
          fontSize: fontSize.lg,
          fontWeight: fontWeight.bold,
          color: C.text,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </div>

      {/* Right slot — kept at tap-width so the title stays optically centered
          whether or not an action is present. */}
      <div
        style={{
          minWidth: size.tap,
          display: 'flex',
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}
      >
        {action}
      </div>
    </div>
  );
}
